import type { ConnectionPhase } from '@parsleyai/ui'
import { useEffect, useMemo } from 'react'
import { useGetMealPlanResults } from '@/store/userData/mealPlanResults/useGetMealPlanResults'
import type { MealPlanResult, StoredMeal } from '@/types/mealPlan'
import { toDinnerView, toPlanView } from './mappers'
import { useMeals } from './useMeals'
import type { DinnerView, PlanView } from './viewModels'

// The view-model read layer. Pages consume plans + dinners ONLY through these hooks — never
// MealPlanResultsContext, the meals store, or the Firestore shapes directly.

// Seed the meals store for a set of mealIds. Keyed by a stable serialization so
// it re-subscribes only when the actual set changes, not on every snapshot.
function useEnsureMeals(mealIds: string[]): void {
  const { ensureMeals } = useMeals()
  const key = mealIds.join(',')
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed by the stable `key` serialization rather than the mealIds array, to avoid re-running ensureMeals on every snapshot-driven array identity change.
  useEffect(() => {
    ensureMeals(mealIds)
  }, [key])
}

function slotMealIds(plan: MealPlanResult): string[] {
  return plan.plan.slots.map((s) => s.mealId).filter((id): id is string => Boolean(id))
}

function dinnersFor(plan: MealPlanResult, getMeal: (mealId: string) => StoredMeal | undefined): DinnerView[] {
  return plan.plan.slots.map((slot, slotIndex) =>
    toDinnerView(slot, slotIndex, slot.mealId ? getMeal(slot.mealId) : undefined, slot.cookId ? plan.cookState?.[slot.cookId] : undefined)
  )
}

// The full plan list as view-models (no dinners), plus the subscription's async
// state so a list page can tell "still loading" from "empty account", and the live
// connection `phase` + `retry` for the reconnecting banner. Order + soft-delete
// filtering come from MealPlanResultsContext.
export function usePlans(): { plans: PlanView[]; loading: boolean; phase: ConnectionPhase; retry: () => void } {
  const { state, phase, retry } = useGetMealPlanResults()
  const plans = useMemo(() => state.mealPlanResults.map(toPlanView), [state.mealPlanResults])
  return { plans, loading: state.loading, phase, retry }
}

// One plan + its slot-ordered dinners. `loading` reflects the plan subscription
// still resolving; a null `plan` after loading means "not found".
export function usePlan(planId: string | null): { plan: PlanView | null; dinners: DinnerView[]; loading: boolean } {
  const { state } = useGetMealPlanResults()
  const { getMeal } = useMeals()

  const result = useMemo(
    () => (planId ? (state.mealPlanResults.find((p) => p.id === planId) ?? null) : null),
    [state.mealPlanResults, planId]
  )
  const mealIds = useMemo(() => (result ? slotMealIds(result) : []), [result])
  useEnsureMeals(mealIds)

  const plan = useMemo(() => (result ? toPlanView(result) : null), [result])
  const dinners = useMemo(() => (result ? dinnersFor(result, getMeal) : []), [result, getMeal])
  return { plan, dinners, loading: state.loading }
}

// Dinners for many plans at once, keyed by plan id — for the fan-out surfaces
// (active-plans list, Past Plans) that can't call usePlan in a loop.
export function useDinnersByPlan(planIds: string[]): Map<string, DinnerView[]> {
  const { state } = useGetMealPlanResults()
  const { getMeal } = useMeals()

  const idsKey = planIds.join(',')
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by idsKey (a stable serialization of planIds) rather than the planIds array identity, so the memo recomputes only when the actual id set changes.
  const plans = useMemo(() => {
    const byId = new Map(state.mealPlanResults.map((p) => [p.id, p]))
    return planIds.map((id) => byId.get(id)).filter((p): p is MealPlanResult => Boolean(p))
  }, [state.mealPlanResults, idsKey])

  const mealIds = useMemo(() => plans.flatMap(slotMealIds), [plans])
  useEnsureMeals(mealIds)

  return useMemo(() => {
    const byPlan = new Map<string, DinnerView[]>()
    for (const plan of plans) byPlan.set(plan.id, dinnersFor(plan, getMeal))
    return byPlan
  }, [plans, getMeal])
}
